import type { IndicatorSeries } from "../types.ts";
import type { PolicyWatchBundle, PolicyWatchItem } from "./adapters/policyWatch.ts";
import {
  buildHousingLeadLagPairs,
  housingPairCoverage,
  scanHousingLeadLag,
  shiftMonth,
  type HousingLeadLagPair,
  type HousingLeadLagScan
} from "./housingLeadLag.ts";
import {
  confirmedHousingPolicyEvents,
  type HousingPolicyBundle,
  type HousingPolicyChannel,
  type HousingPolicyEvent
} from "./housingPolicyRegimes.ts";
import { analyzeDirectionalTransmission, type DirectionalTransmissionEvidence } from "./transmissionEngine.ts";
import { evaluateHypothesis, type HypothesisDefinition, type HypothesisResult } from "./hypothesisEngine.ts";
import { discoverTransmissionCandidates, type DiscoveryCandidate } from "./discoveryEngine.ts";
import { analyzeLocalProjection, type LocalProjectionResult, type ProjectionControlSpec, type ProjectionOutcomeMode } from "./localProjectionEngine.ts";
import { buildPolicyChangeMarketRepricingProxy, validateExternalShockRegistry, type ExternalShockRegistry, type PolicyMarketRepricingProxy } from "./shockIdentification.ts";
import { buildPitEdgeReplay, buildPitReplayAudit, type PitReplayAuditBundle } from "./pitReplay.ts";
import { buildMarketStateBundle, type MarketStateBundle } from "./marketStateEngine.ts";
import { buildNonlinearTransmissionBundle, type NonlinearTransmissionBundle } from "./nonlinearTransmissionEngine.ts";
import { buildHysteresisBundle, type HysteresisBundle } from "./hysteresisEngine.ts";
import { buildRateThresholdKinkBundle, type RateThresholdKinkBundle } from "./rateThresholdKinkEngine.ts";
import { buildSpatialTransmissionBundle, type SpatialTransmissionBundle } from "./spatialTransmissionEngine.ts";
import { buildMarketStateChangePointBundle, type MarketStateChangePointBundle } from "./marketStateChangePointEngine.ts";
import { buildAnalysisDataCapabilityMatrix, type AnalysisDataCapability } from "./analysisDataCapabilities.ts";

export type RelationshipDirection = "positive" | "negative" | "mixed" | "insufficient";
export type PeriodVerdict = "stable" | "strengthening" | "weakening" | "sign_reversal" | "unstable" | "insufficient";
export type RelationshipStability = "stable" | "weak" | "lag_unstable" | "period_sensitive" | "policy_sensitive" | "insufficient";

export interface RelationshipRepresentative {
  correlation: number | null;
  direction: RelationshipDirection;
  windowId: string | null;
  windowLabel: string | null;
  bestLagMonths: number | null;
  minimumSamples: number | null;
}

export interface RelationshipPeriodSnapshot {
  id: "all" | "3y" | "5y" | "10y" | "current_regime" | "previous_regime";
  label: string;
  startMonth: string | null;
  endMonth: string | null;
  completeMonths: number;
  status: "ready" | "limited" | "unavailable";
  warning: string | null;
  representative: RelationshipRepresentative;
}

export interface RollingRelationshipPoint {
  month: string;
  correlation: number | null;
  direction: RelationshipDirection;
  windowLabel: string | null;
  minimumSamples: number | null;
}

export interface MatchedPolicy {
  id: string;
  title: string;
  date: string;
  dateBasis: "effective" | "announced";
  monthDistance: number;
  offsetMonths: number;
  importance: "major" | "minor" | null;
  status: "confirmed" | "candidate";
  direction: string;
  channels: HousingPolicyChannel[];
  sourceUrl: string;
  note: string;
}

export interface StructuralChangeCandidate {
  month: string;
  kind: "sign_reversal" | "strength_change";
  beforeCorrelation: number;
  afterCorrelation: number;
  delta: number;
  score: number;
  matchedPolicies: MatchedPolicy[];
  explanation: string;
}

export interface PolicyRelationshipComparison {
  policyId: string;
  policyTitle: string;
  effectiveMonth: string;
  importance: "major" | "minor";
  policyStatus: "confirmed" | "candidate";
  before: RelationshipRepresentative;
  after: RelationshipRepresentative;
  beforePeriod: { startMonth: string; endMonth: string } | null;
  afterPeriod: { startMonth: string; endMonth: string } | null;
  result: "sign_reversal" | "strengthened" | "weakened" | "stable" | "insufficient";
  explanation: string;
}

export interface StructuralValidation {
  status: "ready" | "insufficient";
  evaluated: number;
  persisted: number;
  persistenceRate: number | null;
  note: string;
}

export interface HousingRelationshipResult {
  id: string;
  label: string;
  positiveMeaning: string;
  negativeMeaning: string;
  periods: RelationshipPeriodSnapshot[];
  periodVerdict: PeriodVerdict;
  periodExplanation: string;
  stability: RelationshipStability;
  stabilityExplanation: string;
  rollingWindowMonths: number;
  rolling: RollingRelationshipPoint[];
  structuralChanges: StructuralChangeCandidate[];
  policyComparisons: PolicyRelationshipComparison[];
  structuralValidation: StructuralValidation;
  directionality: DirectionalTransmissionEvidence;
}

export interface HousingAnalysisSafety {
  status: "ready" | "limited" | "blocked";
  warnings: string[];
  completeMonths: number;
  startMonth: string | null;
  endMonth: string | null;
}

export interface HousingRelationshipAnalysis {
  generatedAt: string;
  safety: HousingAnalysisSafety;
  confirmedPolicies: HousingPolicyEvent[];
  watchedPolicyCandidates: PolicyWatchItem[];
  policyWatchStatus: PolicyWatchBundle["status"] | "not_collected";
  relationships: HousingRelationshipResult[];
  directionalEvidence: DirectionalTransmissionEvidence[];
  localProjections: LocalProjectionResult[];
  identifiedShockLocalProjections: LocalProjectionResult[];
  shockIdentification: { policyEventProxy: PolicyMarketRepricingProxy; externalRegistry: ExternalShockRegistry };
  pitReplayAudit: PitReplayAuditBundle;
  marketStates: MarketStateBundle;
  nonlinearTransmission: NonlinearTransmissionBundle;
  hysteresis: HysteresisBundle;
  rateThresholdKink: RateThresholdKinkBundle;
  spatialTransmission: SpatialTransmissionBundle;
  marketStateChangePoints: MarketStateChangePointBundle;
  dataCapabilityMatrix: AnalysisDataCapability[];
  hypotheses: HypothesisResult[];
  discoveries: DiscoveryCandidate[];
  capabilities: {
    implemented: string[];
    pending: string[];
  };
  note: string;
}

/**
 * Canonical fail-closed placeholder used while the async housing bundle is unavailable.
 * Keep this factory next to the interface so new required analysis fields cannot drift
 * out of sync with App/component fallback objects.
 */
export function createBlockedHousingRelationshipAnalysis(reason = "데이터 로딩 전"): HousingRelationshipAnalysis {
  const externalRegistry = validateExternalShockRegistry({ schemaVersion: 1, updatedAt: null, shocks: [] });
  const policyEventProxy = buildPolicyChangeMarketRepricingProxy({});
  const pitReplayAudit = buildPitReplayAudit({ series: [], housingSeries: [], edgeReplays: [], externalRegistry });
  return {
    generatedAt: new Date(0).toISOString(),
    safety: { status: "blocked", warnings: [reason], completeMonths: 0, startMonth: null, endMonth: null },
    confirmedPolicies: [],
    watchedPolicyCandidates: [],
    policyWatchStatus: "not_collected",
    relationships: [],
    directionalEvidence: [],
    localProjections: [],
    identifiedShockLocalProjections: [],
    shockIdentification: { policyEventProxy, externalRegistry },
    pitReplayAudit,
    marketStates: { generatedAt: new Date(0).toISOString(), results: [], currentHighlights: [], note: reason },
    nonlinearTransmission: { generatedAt: new Date(0).toISOString(), asymmetry: [], rateLevelInteraction: [], policyInteraction: [], note: reason },
    hysteresis: { generatedAt: new Date(0).toISOString(), results: [], note: reason },
    rateThresholdKink: { generatedAt: new Date(0).toISOString(), results: [], note: reason },
    spatialTransmission: { generatedAt: new Date(0).toISOString(), results: [], note: reason },
    marketStateChangePoints: { generatedAt: new Date(0).toISOString(), results: [], robustHighlights: [], note: reason },
    dataCapabilityMatrix: buildAnalysisDataCapabilityMatrix(),
    hypotheses: [],
    discoveries: [],
    capabilities: { implemented: [], pending: [] },
    note: reason
  };
}

function monthIndex(month: string) {
  const [y, m] = month.split("-").map(Number);
  return y * 12 + (m - 1);
}

function monthDistance(a: string, b: string) {
  return monthIndex(a) - monthIndex(b);
}

function median(values: number[]) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function signOf(value: number | null, threshold = 0.05): RelationshipDirection {
  if (value == null || !Number.isFinite(value)) return "insufficient";
  if (value > threshold) return "positive";
  if (value < -threshold) return "negative";
  return "mixed";
}

export function representativeRelationship(scan: HousingLeadLagScan): RelationshipRepresentative {
  const candidates = scan.windows.flatMap((window) => {
    if (window.dominantSign !== "positive" && window.dominantSign !== "negative") return [];
    const values = scan.points
      .filter((point) => point.lagMonths >= window.startLagMonths && point.lagMonths <= window.endLagMonths)
      .filter((point): point is typeof point & { correlation: number } => point.correlation != null && Number.isFinite(point.correlation))
      .map((point) => point.correlation);
    const correlation = median(values);
    if (correlation == null) return [];
    return [{
      correlation,
      direction: signOf(correlation),
      windowId: window.id,
      windowLabel: window.label,
      bestLagMonths: window.bestLagMonths,
      minimumSamples: window.minimumSamples
    } satisfies RelationshipRepresentative];
  });
  const best = [...candidates].sort((a, b) => Math.abs(b.correlation ?? 0) - Math.abs(a.correlation ?? 0))[0];
  return best ?? { correlation: null, direction: "insufficient", windowId: null, windowLabel: null, bestLagMonths: null, minimumSamples: null };
}

function scanPair(pair: HousingLeadLagPair, startMonth: string | null, endMonth: string | null, minimumSamples = 12) {
  return scanHousingLeadLag({
    id: pair.id,
    label: pair.label,
    leader: pair.leader,
    follower: pair.follower,
    maxLagMonths: 12,
    minimumSamples,
    positiveMeaning: pair.positiveMeaning,
    negativeMeaning: pair.negativeMeaning,
    note: pair.note,
    startMonth,
    endMonth,
    leaderTransform: pair.leaderTransform,
    followerTransform: pair.followerTransform
  });
}

function monthsWithin(months: string[], startMonth: string | null, endMonth: string | null) {
  return months.filter((month) => (!startMonth || month >= startMonth) && (!endMonth || month <= endMonth));
}

function periodSnapshot(args: {
  id: RelationshipPeriodSnapshot["id"];
  label: string;
  pair: HousingLeadLagPair;
  months: string[];
  startMonth: string | null;
  endMonth: string | null;
  requestedMonths?: number | null;
}) : RelationshipPeriodSnapshot {
  const complete = monthsWithin(args.months, args.startMonth, args.endMonth);
  const requested = args.requestedMonths ?? null;
  let status: RelationshipPeriodSnapshot["status"] = "ready";
  let warning: string | null = null;
  if (!args.startMonth || !args.endMonth || complete.length < 12) {
    status = "unavailable";
    warning = "비교 가능한 완결월이 너무 적어 이 기간은 아직 분석하지 않습니다.";
  } else if (requested && complete.length < requested) {
    if (complete.length >= Math.max(24, Math.floor(requested * 0.8))) {
      status = "limited";
      warning = `요청한 ${requested}개월보다 완결월이 적어 기간 해석에 주의가 필요합니다 (${complete.length}개월).`;
    } else {
      status = "unavailable";
      warning = `요청한 기간을 채우지 못했습니다 (${complete.length}/${requested}개월). 데이터가 쌓이면 자동 활성화됩니다.`;
    }
  } else if (complete.length < 36) {
    status = "limited";
    warning = `표본기간이 ${complete.length}개월로 짧아 r과 전달구간이 크게 흔들릴 수 있습니다.`;
  }

  const actualStart = complete[0] ?? null;
  const actualEnd = complete.at(-1) ?? null;
  if (status === "unavailable") {
    return { id: args.id, label: args.label, startMonth: actualStart, endMonth: actualEnd, completeMonths: complete.length, status, warning, representative: { correlation: null, direction: "insufficient", windowId: null, windowLabel: null, bestLagMonths: null, minimumSamples: null } };
  }
  const representative = representativeRelationship(scanPair(args.pair, args.startMonth, args.endMonth, 12));
  if (representative.correlation == null && status === "ready") {
    status = "limited";
    warning = warning ?? "전이구간 안에서 같은 부호가 충분히 이어지지 않아 대표 r을 만들지 않습니다.";
  }
  return { id: args.id, label: args.label, startMonth: actualStart, endMonth: actualEnd, completeMonths: complete.length, status, warning, representative };
}

function periodDefinitions(housing: HousingPolicyBundle, months: string[]) {
  const start = months[0] ?? null;
  const end = months.at(-1) ?? null;
  const active = housing.activeRegime;
  const major = [...housing.regimes].filter((regime) => regime.importance === "major").sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  const activeIndex = active ? major.findIndex((regime) => regime.id === active.id) : -1;
  const previous = activeIndex > 0 ? major[activeIndex - 1] : null;
  const recent = (years: number) => end ? shiftMonth(end, -(years * 12 - 1)) : null;
  return [
    { id: "all" as const, label: "전체(MAX)", startMonth: start, endMonth: end, requestedMonths: null },
    { id: "3y" as const, label: "최근 3년", startMonth: recent(3), endMonth: end, requestedMonths: 36 },
    { id: "5y" as const, label: "최근 5년", startMonth: recent(5), endMonth: end, requestedMonths: 60 },
    { id: "10y" as const, label: "최근 10년", startMonth: recent(10), endMonth: end, requestedMonths: 120 },
    { id: "current_regime" as const, label: "현재 정책국면", startMonth: active?.effectiveFrom.slice(0, 7) ?? null, endMonth: end, requestedMonths: null },
    { id: "previous_regime" as const, label: "직전 정책국면", startMonth: previous?.effectiveFrom.slice(0, 7) ?? null, endMonth: previous?.effectiveTo?.slice(0, 7) ?? (active ? shiftMonth(active.effectiveFrom.slice(0, 7), -1) : null), requestedMonths: null }
  ];
}

function periodVerdict(periods: RelationshipPeriodSnapshot[]) : { verdict: PeriodVerdict; explanation: string } {
  const full = periods.find((period) => period.id === "all")?.representative;
  const comparators = ["current_regime", "3y", "5y", "10y"]
    .map((id) => periods.find((period) => period.id === id && period.representative.correlation != null))
    .filter((period): period is RelationshipPeriodSnapshot => Boolean(period));
  const recent = comparators[0];
  if (!full || full.correlation == null || !recent || recent.representative.correlation == null) {
    return { verdict: "insufficient", explanation: "전체기간과 최근/정책국면을 동시에 비교할 표본이 아직 충분하지 않습니다. 데이터가 쌓이면 자동으로 다시 판정합니다." };
  }
  const a = full.correlation;
  const b = recent.representative.correlation;
  const signFlip = a * b < 0 && Math.abs(a) >= 0.20 && Math.abs(b) >= 0.20;
  if (signFlip) return { verdict: "sign_reversal", explanation: `${recent.label}의 관계 방향이 전체기간과 반대로 나타납니다. 구조변화 후보로 보되 정책·경기·구성효과를 분리하기 전에는 인과로 단정하지 않습니다.` };
  if (Math.sign(a) === Math.sign(b) && Math.abs(b) - Math.abs(a) >= 0.20) return { verdict: "strengthening", explanation: `${recent.label}에서 전체기간보다 같은 방향의 관계가 뚜렷해졌습니다.` };
  if (Math.sign(a) === Math.sign(b) && Math.abs(a) - Math.abs(b) >= 0.20) return { verdict: "weakening", explanation: `${recent.label}에서 전체기간보다 관계가 약해졌습니다.` };
  if (Math.sign(a) === Math.sign(b)) return { verdict: "stable", explanation: `${recent.label}과 전체기간의 관계 방향이 대체로 같습니다.` };
  return { verdict: "unstable", explanation: "기간에 따라 관계 방향이 달라지지만 강도가 약하거나 표본이 제한적이어서 구조적 반전으로 승격하지 않습니다." };
}


function stabilityAssessment(periods: RelationshipPeriodSnapshot[], verdict: PeriodVerdict): { stability: RelationshipStability; explanation: string } {
  const full = periods.find((period) => period.id === "all");
  const current = periods.find((period) => period.id === "current_regime");
  const previous = periods.find((period) => period.id === "previous_regime");
  const allR = full?.representative.correlation ?? null;
  if (allR == null) {
    if ((full?.completeMonths ?? 0) >= 12) return { stability: "lag_unstable", explanation: "전체기간 표본은 있지만 여러 lag에서 같은 방향이 충분히 이어지지 않아 대표 관계를 만들지 못했습니다." };
    return { stability: "insufficient", explanation: "관계 안정성을 평가할 완결월이 아직 충분하지 않습니다." };
  }
  const currentR = current?.representative.correlation ?? null;
  const previousR = previous?.representative.correlation ?? null;
  if (currentR != null && previousR != null) {
    const strongFlip = currentR * previousR < 0 && Math.abs(currentR) >= 0.20 && Math.abs(previousR) >= 0.20;
    const largeShift = Math.abs(currentR - previousR) >= 0.35;
    if (strongFlip || largeShift) return { stability: "policy_sensitive", explanation: "현재와 직전 대정책 국면의 관계가 크게 달라 정책국면에 민감한 관계로 분류합니다. 정책 인과효과를 뜻하지는 않습니다." };
  }
  if (verdict === "sign_reversal" || verdict === "unstable") return { stability: "period_sensitive", explanation: "전체기간과 최근기간의 방향이 달라 분석기간 선택에 민감합니다." };
  if (Math.abs(allR) < 0.15) return { stability: "weak", explanation: "전체기간 대표 r의 절대값이 작아 방향보다 관계 약함을 우선 표시합니다." };
  return { stability: "stable", explanation: "현재 확보된 기간·전이구간 비교에서 관계 방향이 대체로 유지됩니다." };
}

function rollingRelationship(pair: HousingLeadLagPair, months: string[], rollingWindowMonths = 24): RollingRelationshipPoint[] {
  if (!months.length) return [];
  const monthSet = new Set(months);
  return months.map((endMonth) => {
    const startMonth = shiftMonth(endMonth, -(rollingWindowMonths - 1));
    const expected = Array.from({ length: rollingWindowMonths }, (_, index) => shiftMonth(startMonth, index));
    const completeCount = expected.filter((month) => monthSet.has(month)).length;
    if (completeCount < rollingWindowMonths) {
      return { month: endMonth, correlation: null, direction: "insufficient" as const, windowLabel: null, minimumSamples: null };
    }
    const rep = representativeRelationship(scanPair(pair, startMonth, endMonth, 12));
    return { month: endMonth, correlation: rep.correlation, direction: rep.direction, windowLabel: rep.windowLabel, minimumSamples: rep.minimumSamples };
  });
}

function candidatePolicyEvents(policyWatch: PolicyWatchBundle | null): Array<HousingPolicyEvent | PolicyWatchItem> {
  return [
    ...confirmedHousingPolicyEvents(),
    ...(policyWatch?.items ?? []).filter((item) => item.classification !== "watch" && (item.effectiveDate || item.announcedDate))
  ];
}

function policyDate(item: HousingPolicyEvent | PolicyWatchItem): { date: string; basis: "effective" | "announced" } | null {
  if ("status" in item) {
    if (item.effectiveFrom) return { date: item.effectiveFrom, basis: "effective" };
    if (item.announcedAt) return { date: item.announcedAt, basis: "announced" };
    return null;
  }
  if (item.effectiveDate) return { date: item.effectiveDate, basis: "effective" };
  if (item.announcedDate) return { date: item.announcedDate, basis: "announced" };
  return null;
}

function matchesChannel(pair: HousingLeadLagPair, channels: HousingPolicyChannel[]) {
  const preferred: HousingPolicyChannel[] = pair.id.startsWith("mortgage-to-volume")
    ? ["mortgage_cap", "ltv", "dsr", "regulated_area", "funding"]
    : ["mortgage_cap", "ltv", "dsr", "regulated_area", "supply", "tax"];
  return channels.some((channel) => preferred.includes(channel));
}

function matchPolicies(month: string, pair: HousingLeadLagPair, policyWatch: PolicyWatchBundle | null): MatchedPolicy[] {
  const target = monthIndex(month);
  const matched: MatchedPolicy[] = [];
  for (const item of candidatePolicyEvents(policyWatch)) {
    const policyDateInfo = policyDate(item);
    if (!policyDateInfo) continue;
    const { date, basis: dateBasis } = policyDateInfo;
    const policyMonth = monthIndex(date.slice(0, 7));
    const offsetMonths = policyMonth - target;
    const d = Math.abs(offsetMonths);
    const channels = item.channels ?? [];
    if (d > 6 || (channels.length > 0 && !matchesChannel(pair, channels))) continue;
    if ("status" in item) {
      matched.push({ id: item.id, title: item.title, date, dateBasis, monthDistance: d, offsetMonths, importance: item.importance, status: "confirmed", direction: item.direction, channels, sourceUrl: item.sourceUrl, note: item.note });
    } else {
      matched.push({ id: item.id, title: item.title, date, dateBasis, monthDistance: d, offsetMonths, importance: item.importance, status: "candidate", direction: item.direction, channels, sourceUrl: item.link, note: "공식 정책 소스에서 자동수집한 미검증 정책 후보입니다. 확정 정책 레짐이나 인과근거로 사용하지 않습니다." });
    }
  }
  return matched.sort((a, b) => {
    if (a.status !== b.status) return a.status === "confirmed" ? -1 : 1;
    if (a.importance !== b.importance) {
      const rank = (value: MatchedPolicy["importance"]) => value === "major" ? 0 : value === "minor" ? 1 : 2;
      return rank(a.importance) - rank(b.importance);
    }
    return a.monthDistance - b.monthDistance;
  }).slice(0, 3);
}

export function detectStructuralCandidatesFromRolling(pair: HousingLeadLagPair, rolling: RollingRelationshipPoint[], policyWatch: PolicyWatchBundle | null): StructuralChangeCandidate[] {
  const valid = rolling.filter((point): point is RollingRelationshipPoint & { correlation: number } => point.correlation != null && Number.isFinite(point.correlation));
  const raw: StructuralChangeCandidate[] = [];
  for (let i = 0; i < valid.length; i++) {
    const current = valid[i];
    const priorTarget = shiftMonth(current.month, -6);
    const prior = valid.find((point) => point.month === priorTarget);
    if (!prior) continue;
    const delta = current.correlation - prior.correlation;
    const signFlip = current.correlation * prior.correlation < 0 && Math.abs(current.correlation) >= 0.20 && Math.abs(prior.correlation) >= 0.20;
    const strengthChange = Math.abs(delta) >= 0.35 && Math.max(Math.abs(current.correlation), Math.abs(prior.correlation)) >= 0.30;
    if (!signFlip && !strengthChange) continue;
    const kind = signFlip ? "sign_reversal" as const : "strength_change" as const;
    const matchedPolicies = matchPolicies(current.month, pair, policyWatch);
    raw.push({
      month: current.month,
      kind,
      beforeCorrelation: prior.correlation,
      afterCorrelation: current.correlation,
      delta,
      score: Math.abs(delta) + (signFlip ? 0.25 : 0),
      matchedPolicies,
      explanation: signFlip
        ? `24개월 rolling 관계가 약 6개월 전과 비교해 ${prior.correlation.toFixed(2)} → ${current.correlation.toFixed(2)}로 부호가 바뀌었습니다. 구조적 반전 후보이지 원인 확정은 아닙니다.`
        : `24개월 rolling 관계가 약 6개월 사이 ${prior.correlation.toFixed(2)} → ${current.correlation.toFixed(2)}로 크게 달라졌습니다. 관계 강도 변화 후보입니다.`
    });
  }
  const selected: StructuralChangeCandidate[] = [];
  for (const candidate of [...raw].sort((a, b) => b.score - a.score)) {
    if (selected.some((existing) => Math.abs(monthDistance(existing.month, candidate.month)) <= 6)) continue;
    selected.push(candidate);
  }
  return selected.sort((a, b) => b.month.localeCompare(a.month)).slice(0, 5);
}

function comparisonResult(before: RelationshipRepresentative, after: RelationshipRepresentative): PolicyRelationshipComparison["result"] {
  if (before.correlation == null || after.correlation == null) return "insufficient";
  const a = before.correlation;
  const b = after.correlation;
  if (a * b < 0 && Math.abs(a) >= 0.20 && Math.abs(b) >= 0.20) return "sign_reversal";
  if (Math.sign(a) === Math.sign(b) && Math.abs(b) - Math.abs(a) >= 0.20) return "strengthened";
  if (Math.sign(a) === Math.sign(b) && Math.abs(a) - Math.abs(b) >= 0.20) return "weakened";
  return "stable";
}

function policyComparisons(pair: HousingLeadLagPair, housing: HousingPolicyBundle, coverageStart: string | null, coverageEnd: string | null): PolicyRelationshipComparison[] {
  if (!coverageStart || !coverageEnd) return [];
  const policies = confirmedHousingPolicyEvents().filter((event) => event.effectiveFrom).sort((a, b) => (a.effectiveFrom ?? "").localeCompare(b.effectiveFrom ?? ""));
  return policies.map((policy, index) => {
    const effectiveMonth = policy.effectiveFrom!.slice(0, 7);
    const nextMajor = policies.slice(index + 1).find((event) => event.importance === "major")?.effectiveFrom?.slice(0, 7) ?? null;
    const beforeStart = [coverageStart, shiftMonth(effectiveMonth, -18)].sort().at(-1)!;
    const beforeEnd = shiftMonth(effectiveMonth, -1);
    const nominalAfterEnd = shiftMonth(effectiveMonth, policy.importance === "major" ? 17 : 11);
    const afterEndCandidates = [coverageEnd, nominalAfterEnd, ...(nextMajor ? [shiftMonth(nextMajor, -1)] : [])].sort();
    const afterEnd = afterEndCandidates[0];
    const beforePeriod = beforeStart <= beforeEnd ? { startMonth: beforeStart, endMonth: beforeEnd } : null;
    const afterPeriod = effectiveMonth <= afterEnd ? { startMonth: effectiveMonth, endMonth: afterEnd } : null;
    const before = beforePeriod ? representativeRelationship(scanPair(pair, beforePeriod.startMonth, beforePeriod.endMonth, 12)) : representativeRelationship(scanPair(pair, null, null, 9999));
    const after = afterPeriod ? representativeRelationship(scanPair(pair, afterPeriod.startMonth, afterPeriod.endMonth, 12)) : representativeRelationship(scanPair(pair, null, null, 9999));
    const result = comparisonResult(before, after);
    const explanation = result === "sign_reversal"
      ? `정책 시행 전후 대표 관계의 부호가 ${before.correlation?.toFixed(2)} → ${after.correlation?.toFixed(2)}로 반전되었습니다. “정책 이후 반전” 후보이며 정책 때문에 반전됐다고 단정하지 않습니다.`
      : result === "strengthened"
        ? `정책 시행 후 같은 방향 관계가 더 강해졌습니다 (${before.correlation?.toFixed(2)} → ${after.correlation?.toFixed(2)}).`
        : result === "weakened"
          ? `정책 시행 후 관계가 약해졌습니다 (${before.correlation?.toFixed(2)} → ${after.correlation?.toFixed(2)}).`
          : result === "stable"
            ? `정책 시행 전후 관계의 방향·강도 차이가 현재 기준에서는 크지 않습니다 (${before.correlation?.toFixed(2)} → ${after.correlation?.toFixed(2)}).`
            : "정책 전·후 양쪽에 최소 표본이 충분하지 않아 비교를 보류합니다. 후속 데이터가 쌓이면 자동 재계산됩니다.";
    return { policyId: policy.id, policyTitle: policy.title, effectiveMonth, importance: policy.importance, policyStatus: "confirmed" as const, before, after, beforePeriod, afterPeriod, result, explanation };
  }).reverse();
}

function watchedPolicyComparisons(pair: HousingLeadLagPair, policyWatch: PolicyWatchBundle | null, coverageStart: string | null, coverageEnd: string | null): PolicyRelationshipComparison[] {
  if (!policyWatch || !coverageStart || !coverageEnd) return [];
  return policyWatch.items
    .filter((item) => item.classification !== "watch" && item.importance != null && item.effectiveDate != null)
    .filter((item) => matchesChannel(pair, item.channels))
    .map((item) => {
      const effectiveMonth = item.effectiveDate!.slice(0, 7);
      const beforeStart = [coverageStart, shiftMonth(effectiveMonth, -12)].sort().at(-1)!;
      const beforeEnd = shiftMonth(effectiveMonth, -1);
      const afterEnd = [coverageEnd, shiftMonth(effectiveMonth, 11)].sort()[0];
      const beforePeriod = beforeStart <= beforeEnd ? { startMonth: beforeStart, endMonth: beforeEnd } : null;
      const afterPeriod = effectiveMonth <= afterEnd ? { startMonth: effectiveMonth, endMonth: afterEnd } : null;
      const before = beforePeriod ? representativeRelationship(scanPair(pair, beforePeriod.startMonth, beforePeriod.endMonth, 12)) : representativeRelationship(scanPair(pair, null, null, 9999));
      const after = afterPeriod ? representativeRelationship(scanPair(pair, afterPeriod.startMonth, afterPeriod.endMonth, 12)) : representativeRelationship(scanPair(pair, null, null, 9999));
      const result = comparisonResult(before, after);
      const explanation = result === "sign_reversal"
        ? `자동수집 정책 후보의 시행 후보일 전후 대표 관계가 ${before.correlation?.toFixed(2)} → ${after.correlation?.toFixed(2)}로 반전되었습니다. 미검증 정책 후보와 함께 관찰된 변화일 뿐, 확정 레짐이나 인과효과로 승격하지 않습니다.`
        : result === "strengthened"
          ? `자동수집 정책 후보 이후 관계가 더 강해졌습니다 (${before.correlation?.toFixed(2)} → ${after.correlation?.toFixed(2)}). 후보 자체가 미검증이므로 참고 신호로만 사용합니다.`
          : result === "weakened"
            ? `자동수집 정책 후보 이후 관계가 약해졌습니다 (${before.correlation?.toFixed(2)} → ${after.correlation?.toFixed(2)}). 후보 자체가 미검증이므로 참고 신호로만 사용합니다.`
            : result === "stable"
              ? `자동수집 정책 후보 전후에 큰 관계 변화는 확인되지 않습니다 (${before.correlation?.toFixed(2)} → ${after.correlation?.toFixed(2)}).`
              : "자동수집 정책 후보의 전·후 표본이 아직 부족해 비교를 보류합니다. 후보는 확정 숫자 레짐을 변경하지 않습니다.";
      return { policyId: item.id, policyTitle: item.title, effectiveMonth, importance: item.importance!, policyStatus: "candidate" as const, before, after, beforePeriod, afterPeriod, result, explanation };
    })
    .sort((a, b) => b.effectiveMonth.localeCompare(a.effectiveMonth))
    .slice(0, 20);
}

function validateStructuralCandidates(candidates: StructuralChangeCandidate[], rolling: RollingRelationshipPoint[]): StructuralValidation {
  let evaluated = 0;
  let persisted = 0;
  for (const candidate of candidates) {
    const targetStart = shiftMonth(candidate.month, 6);
    const targetEnd = shiftMonth(candidate.month, 12);
    const future = rolling.filter((point): point is RollingRelationshipPoint & { correlation: number } => point.month >= targetStart && point.month <= targetEnd && point.correlation != null);
    if (future.length < 4) continue;
    evaluated += 1;
    const futureMedian = median(future.map((point) => point.correlation));
    if (futureMedian != null && Math.sign(futureMedian) === Math.sign(candidate.afterCorrelation) && Math.abs(futureMedian) >= 0.15) persisted += 1;
  }
  if (evaluated < 3) return { status: "insufficient", evaluated, persisted, persistenceRate: null, note: "구조변화 탐지 자체의 과거검증은 평가 가능한 과거 후보가 3개 이상 쌓일 때 자동 활성화됩니다. 현재는 적중률을 만들지 않습니다." };
  return { status: "ready", evaluated, persisted, persistenceRate: persisted / evaluated, note: "탐지 후 6~12개월에도 같은 방향 관계가 유지됐는지 보는 지속성 검증입니다. 정책 인과나 가격예측 정확도가 아닙니다." };
}

function buildSafety(housing: HousingPolicyBundle, mortgageRate: IndicatorSeries, pairs: HousingLeadLagPair[]) : HousingAnalysisSafety {
  const warnings: string[] = [];
  const mortgageValues = mortgageRate.observations.filter((observation) => observation.value != null && Number.isFinite(observation.value));
  if (mortgageValues.length < 12) {
    return { status: "blocked", warnings: ["주담대금리 유효 관측치가 12개 미만이라 금리→주택 관계 분석을 중지합니다. 값을 보간하거나 다른 금리로 대체하지 않습니다."], completeMonths: 0, startMonth: null, endMonth: null };
  }
  if (mortgageRate.unit !== "%" || mortgageValues.some((observation) => observation.value! < 0 || observation.value! > 30)) {
    return { status: "blocked", warnings: ["주담대금리 단위 또는 값 범위가 예상 정의와 맞지 않아 관계 분석을 안전중지했습니다. 원자료 정의를 확인하기 전까지 자동 보정하지 않습니다."], completeMonths: 0, startMonth: null, endMonth: null };
  }
  if (!pairs.length) return { status: "blocked", warnings: ["검증된 결과변수 시계열이 없어 관계 분석을 보류합니다."], completeMonths: 0, startMonth: null, endMonth: null };

  const coverages = pairs.map(housingPairCoverage).filter((coverage) => coverage.count > 0);
  const broadest = [...coverages].sort((a, b) => b.count - a.count)[0] ?? { months: [], startMonth: null, endMonth: null, count: 0 };
  const rtms = housing.transactionModule.rtms;
  if (!rtms || rtms.status === "error" || rtms.status === "not_collected") {
    warnings.push("RTMS 가격대별 경로는 N/A이지만, 공식 주택가격지수 경로가 있으면 그 경로는 독립적으로 계속 분석합니다.");
  } else {
    if (rtms.collection.errors.length > 0) warnings.push(`최근 RTMS 수집에 ${rtms.collection.errors.length}개 오류가 있어 성공한 완결월만 사용합니다.`);
    const verifiedStart = rtms.coverage.startMonth ?? null;
    if (verifiedStart && verifiedStart >= "2024-01") warnings.push(`RTMS 역사 지역코드 유효기간은 현재 ${verifiedStart}부터 검증되어 있습니다. 그 이전을 MAX 완료로 가장하지 않고 별도 historical coverage blocker로 유지합니다.`);
  }
  if (broadest.count < 24) warnings.push(`가장 긴 결과변수 관측기간도 ${broadest.count}개월이라 24개월 rolling 구조변화 분석이 제한됩니다.`);
  const status: HousingAnalysisSafety["status"] = broadest.count < 12 ? "blocked" : broadest.count < 36 || warnings.length ? "limited" : "ready";
  return { status, warnings, completeMonths: broadest.count, startMonth: broadest.startMonth, endMonth: broadest.endMonth };
}

export function analyzeHousingRelationshipPair(args: {
  pair: HousingLeadLagPair;
  housing: HousingPolicyBundle;
  policyWatch: PolicyWatchBundle | null;
}): HousingRelationshipResult {
  const pairCoverage = housingPairCoverage(args.pair);
  const completeMonths = pairCoverage.months;
  const definitions = periodDefinitions(args.housing, completeMonths);
  const periods = definitions.map((definition) => periodSnapshot({ ...definition, pair: args.pair, months: completeMonths }));
  const periodAssessment = periodVerdict(periods);
  const stability = stabilityAssessment(periods, periodAssessment.verdict);
  const rolling = rollingRelationship(args.pair, completeMonths, 24);
  const changes = detectStructuralCandidatesFromRolling(args.pair, rolling, args.policyWatch);
  const coverageStart = completeMonths[0] ?? null;
  const coverageEnd = completeMonths.at(-1) ?? null;
  const comparisons = [
    ...policyComparisons(args.pair, args.housing, coverageStart, coverageEnd),
    ...watchedPolicyComparisons(args.pair, args.policyWatch, coverageStart, coverageEnd)
  ].sort((a, b) => b.effectiveMonth.localeCompare(a.effectiveMonth));
  const directionality = analyzeDirectionalTransmission({
    id: args.pair.id,
    label: args.pair.label,
    leader: args.pair.leader,
    follower: args.pair.follower,
    leaderTransform: args.pair.leaderTransform,
    followerTransform: args.pair.followerTransform,
    maxLagMonths: 12,
    minimumSamples: 12,
    startMonth: coverageStart,
    endMonth: coverageEnd,
    note: args.pair.note
  });
  return {
    id: args.pair.id,
    label: args.pair.label,
    positiveMeaning: args.pair.positiveMeaning,
    negativeMeaning: args.pair.negativeMeaning,
    periods,
    periodVerdict: periodAssessment.verdict,
    periodExplanation: periodAssessment.explanation,
    stability: stability.stability,
    stabilityExplanation: stability.explanation,
    rollingWindowMonths: 24,
    rolling,
    structuralChanges: changes,
    policyComparisons: comparisons,
    structuralValidation: validateStructuralCandidates(changes, rolling),
    directionality
  };
}


function hasUsableObservations(series: IndicatorSeries | null | undefined) {
  return Boolean(series && series.observations.filter((observation) => observation.value != null && Number.isFinite(observation.value)).length >= 12);
}

type ContextEdgeSpec = {
  id: string;
  label: string;
  leader: IndicatorSeries | null | undefined;
  follower: IndicatorSeries | null | undefined;
  note: string;
};

function contextEdgeSpecs(args: {
  housing: HousingPolicyBundle;
  mortgageRate: IndicatorSeries;
  benchmarkRate?: IndicatorSeries | null;
  policyRate?: IndicatorSeries | null;
  policyRateDaily?: IndicatorSeries | null;
  eventMarketRateDaily?: IndicatorSeries | null;
  fundingRate?: IndicatorSeries | null;
  creditSpread?: IndicatorSeries | null;
  economicSentiment?: IndicatorSeries | null;
  industrialProduction?: IndicatorSeries | null;
  officialHousingPrices?: IndicatorSeries[];
}): ContextEdgeSpec[] {
  const specs: ContextEdgeSpec[] = [
    { id: "edge-market5y-to-funding", label: "국고채5Y → 은행 금융채 신규조달", leader: args.benchmarkRate, follower: args.fundingRate, note: "시장금리가 은행 신규조달비용으로 이어지는 금융전달 edge입니다." },
    { id: "edge-funding-to-mortgage", label: "은행 금융채 신규조달 → 신규 주담대금리", leader: args.fundingRate, follower: args.mortgageRate, note: "은행 조달비용이 신규 주담대 pricing으로 이어지는 edge입니다." },
    { id: "edge-market5y-to-policy", label: "국고채5Y → 한국은행 기준금리", leader: args.benchmarkRate, follower: args.policyRate, note: "시장금리가 정책금리를 선행하는지와 반대방향을 같은 규칙으로 비교합니다. 정책내생성 때문에 causal edge로 자동 승격하지 않습니다." },
    { id: "edge-market5y-to-credit-spread", label: "국고채5Y → 기업 신용스프레드", leader: args.benchmarkRate, follower: args.creditSpread, note: "금리충격이 신용위험/위험프리미엄으로 이어지는 간접 경기경로 후보입니다." },
    { id: "edge-credit-spread-to-sentiment", label: "기업 신용스프레드 → 경제심리", leader: args.creditSpread, follower: args.economicSentiment, note: "신용여건 악화가 심리로 이어지는 경기경로 후보입니다." },
    { id: "edge-sentiment-to-production", label: "경제심리 → 전산업생산", leader: args.economicSentiment, follower: args.industrialProduction, note: "심리 변화가 실물활동으로 이어지는 경기경로 후보입니다." }
  ];
  const capitalPrice = (args.officialHousingPrices ?? []).find((series) => series.name.includes("수도권")) ?? null;
  if (capitalPrice) specs.push({ id: "edge-production-to-capital-price", label: "전산업생산 → 수도권 공식 아파트가격", leader: args.industrialProduction, follower: capitalPrice, note: "경기활동과 주택가격의 간접경로 후보입니다. 자산가격이 경기를 선행할 수도 있어 역방향 scan을 필수로 봅니다." });
  return specs;
}

function buildContextDirectionalEvidence(args: {
  housing: HousingPolicyBundle;
  mortgageRate: IndicatorSeries;
  benchmarkRate?: IndicatorSeries | null;
  policyRate?: IndicatorSeries | null;
  policyRateDaily?: IndicatorSeries | null;
  eventMarketRateDaily?: IndicatorSeries | null;
  fundingRate?: IndicatorSeries | null;
  creditSpread?: IndicatorSeries | null;
  economicSentiment?: IndicatorSeries | null;
  industrialProduction?: IndicatorSeries | null;
  officialHousingPrices?: IndicatorSeries[];
}) {
  return contextEdgeSpecs(args).flatMap((spec) => {
    if (!hasUsableObservations(spec.leader) || !hasUsableObservations(spec.follower)) return [];
    return [analyzeDirectionalTransmission({
      id: spec.id,
      label: spec.label,
      leader: spec.leader!,
      follower: spec.follower!,
      leaderTransform: "first_difference",
      followerTransform: "first_difference",
      maxLagMonths: 12,
      minimumSamples: 12,
      note: spec.note
    })];
  });
}

function projectionOutcomeMode(series: IndicatorSeries): ProjectionOutcomeMode {
  if (["count", "housing_units", "eok_krw", "krw_per_usd"].includes(series.unit)) return "log_level_change_pct";
  if (series.unit === "index" && (series.layer === "real_estate" || /생산|production/i.test(`${series.name} ${series.id}`))) return "log_level_change_pct";
  return "level_change";
}

function projectionControl(id: string, label: string, series: IndicatorSeries | null | undefined, transform: "first_difference" | "yoy_log_change" = "first_difference"): ProjectionControlSpec | null {
  if (!hasUsableObservations(series)) return null;
  return { id, label, series: series!, transform, lagMonths: 1 };
}

function projectionControlsForEdge(edgeId: string, args: {
  mortgageRate: IndicatorSeries;
  benchmarkRate?: IndicatorSeries | null;
  policyRate?: IndicatorSeries | null;
  policyRateDaily?: IndicatorSeries | null;
  eventMarketRateDaily?: IndicatorSeries | null;
  fundingRate?: IndicatorSeries | null;
  creditSpread?: IndicatorSeries | null;
  economicSentiment?: IndicatorSeries | null;
  industrialProduction?: IndicatorSeries | null;
}) {
  const controls = {
    mortgage: projectionControl("ctrl-mortgage", "주담대금리", args.mortgageRate),
    market: projectionControl("ctrl-market5y", "국고채5Y", args.benchmarkRate),
    policy: projectionControl("ctrl-policy", "기준금리", args.policyRate),
    funding: projectionControl("ctrl-funding", "은행 신규조달금리", args.fundingRate),
    credit: projectionControl("ctrl-credit", "기업 신용스프레드", args.creditSpread),
    sentiment: projectionControl("ctrl-sentiment", "경제심리", args.economicSentiment),
    production: projectionControl("ctrl-production", "전산업생산", args.industrialProduction)
  };
  let selected: Array<ProjectionControlSpec | null>;
  if (edgeId === "edge-market5y-to-policy") selected = [controls.credit, controls.sentiment, controls.production];
  else if (edgeId === "edge-market5y-to-funding") selected = [controls.policy, controls.credit, controls.sentiment];
  else if (edgeId === "edge-funding-to-mortgage") selected = [controls.market, controls.policy, controls.credit];
  else if (edgeId === "edge-market5y-to-credit-spread") selected = [controls.policy, controls.sentiment, controls.production];
  else if (edgeId === "edge-credit-spread-to-sentiment") selected = [controls.market, controls.policy, controls.production];
  else if (edgeId === "edge-sentiment-to-production") selected = [controls.market, controls.policy, controls.credit];
  else if (edgeId === "edge-production-to-capital-price") selected = [controls.market, controls.policy, controls.credit, controls.sentiment];
  else if (edgeId.startsWith("mortgage-to-")) selected = [controls.market, controls.policy, controls.credit, controls.sentiment, controls.production];
  else if (edgeId.includes("volume-to-price")) selected = [controls.mortgage, controls.market, controls.policy, controls.sentiment, controls.production];
  else selected = [controls.market, controls.policy, controls.credit, controls.sentiment, controls.production];
  const seen = new Set<string>();
  return selected.flatMap((control) => {
    if (!control || seen.has(control.series.id)) return [];
    seen.add(control.series.id);
    return [control];
  });
}

function buildLocalProjectionResults(args: {
  housing: HousingPolicyBundle;
  mortgageRate: IndicatorSeries;
  benchmarkRate?: IndicatorSeries | null;
  policyRate?: IndicatorSeries | null;
  policyRateDaily?: IndicatorSeries | null;
  eventMarketRateDaily?: IndicatorSeries | null;
  fundingRate?: IndicatorSeries | null;
  creditSpread?: IndicatorSeries | null;
  economicSentiment?: IndicatorSeries | null;
  industrialProduction?: IndicatorSeries | null;
  officialHousingPrices?: IndicatorSeries[];
}, pairs: HousingLeadLagPair[]) {
  const pairSpecs = pairs
    .filter((pair) => pair.segment === "official_price" || (pair.segment === "market_band" && /m3_30_50|m4_ge_50/.test(pair.id)))
    .map((pair) => ({
      id: pair.id,
      label: pair.label,
      leader: pair.leader,
      follower: pair.follower,
      leaderTransform: pair.leaderTransform ?? "first_difference" as const,
      startMonth: housingPairCoverage(pair).startMonth,
      endMonth: housingPairCoverage(pair).endMonth,
      note: pair.note
    }));
  const contextSpecs = contextEdgeSpecs(args).flatMap((spec) => hasUsableObservations(spec.leader) && hasUsableObservations(spec.follower) ? [{
    ...spec,
    leader: spec.leader!,
    follower: spec.follower!,
    leaderTransform: "first_difference" as const,
    startMonth: null,
    endMonth: null
  }] : []);

  return [...contextSpecs, ...pairSpecs].map((spec) => {
    const controls = projectionControlsForEdge(spec.id, args);
    return analyzeLocalProjection({
      id: spec.id,
      label: spec.label,
      shock: spec.leader,
      outcome: spec.follower,
      shockTransform: spec.leaderTransform,
      outcomeMode: projectionOutcomeMode(spec.follower),
      controls,
      innovationPredictors: controls,
      maxHorizonMonths: 12,
      minimumSamples: 36,
      startMonth: spec.startMonth,
      endMonth: spec.endMonth,
      note: `${spec.note} Local Projection은 관측 월간 shock과 lagged common controls를 이용한 관찰적 보강검증이며 외생 surprise가 아닙니다.`
    });
  });
}

function buildIdentifiedShockLocalProjections(args: {
  mortgageRate: IndicatorSeries;
  policyRateDaily?: IndicatorSeries | null;
  eventMarketRateDaily?: IndicatorSeries | null;
  fundingRate?: IndicatorSeries | null;
  creditSpread?: IndicatorSeries | null;
  economicSentiment?: IndicatorSeries | null;
  industrialProduction?: IndicatorSeries | null;
  officialHousingPrices?: IndicatorSeries[];
  externalShockRegistry: ExternalShockRegistry;
}) {
  const policyEventProxy = buildPolicyChangeMarketRepricingProxy({ policyRateDaily: args.policyRateDaily, marketRateDaily: args.eventMarketRateDaily });
  const capitalPrice = (args.officialHousingPrices ?? []).find((series) => series.name.includes("수도권")) ?? null;
  const targets = [
    hasUsableObservations(args.fundingRate) ? { suffix: "funding", label: "은행 신규조달금리", series: args.fundingRate!, controlEdgeId: "edge-market5y-to-funding" } : null,
    hasUsableObservations(args.mortgageRate) ? { suffix: "mortgage", label: "신규 주담대금리", series: args.mortgageRate, controlEdgeId: "edge-funding-to-mortgage" } : null,
    hasUsableObservations(capitalPrice) ? { suffix: "capital-price", label: "수도권 공식 아파트가격", series: capitalPrice!, controlEdgeId: "edge-production-to-capital-price" } : null
  ].filter((item): item is NonNullable<typeof item> => Boolean(item));
  const results: LocalProjectionResult[] = [];
  if (policyEventProxy.status !== "unavailable" && policyEventProxy.matchedEvents >= 8) {
    for (const target of targets) {
      const controls = projectionControlsForEdge(target.controlEdgeId, {
        mortgageRate: args.mortgageRate,
        benchmarkRate: null,
        policyRate: null,
        fundingRate: args.fundingRate,
        creditSpread: args.creditSpread,
        economicSentiment: args.economicSentiment,
        industrialProduction: args.industrialProduction
      });
      results.push(analyzeLocalProjection({
        id: `identified-event-bok-ktb1-to-${target.suffix}`,
        label: `BOK 변경일 KTB1Y 재평가 → ${target.label}`,
        shock: policyEventProxy.monthlySeries,
        outcome: target.series,
        shockTransform: "level",
        outcomeMode: projectionOutcomeMode(target.series),
        controls,
        innovationPredictors: [],
        identifiedShock: {
          method: "market_event_proxy",
          causalReadiness: "event_proxy_only",
          pointInTimeSafe: false,
          note: policyEventProxy.note
        },
        maxHorizonMonths: 12,
        minimumSamples: 36,
        note: "정책금리 변경일의 KTB1Y 같은 날 재평가를 shock proxy로 사용합니다. 동결회의 surprise와 사전 기대를 분리하지 못하므로 event_proxy_only입니다."
      }));
    }
  }
  for (const external of args.externalShockRegistry.eligible) {
    if (!external.series) continue;
    for (const target of targets) {
      const controls = projectionControlsForEdge(target.controlEdgeId, {
        mortgageRate: args.mortgageRate,
        benchmarkRate: null,
        policyRate: null,
        fundingRate: args.fundingRate,
        creditSpread: args.creditSpread,
        economicSentiment: args.economicSentiment,
        industrialProduction: args.industrialProduction
      });
      results.push(analyzeLocalProjection({
        id: `identified-${external.id}-to-${target.suffix}`,
        label: `${external.label} → ${target.label}`,
        shock: external.series,
        outcome: target.series,
        shockTransform: "level",
        outcomeMode: projectionOutcomeMode(target.series),
        controls,
        innovationPredictors: [],
        identifiedShock: {
          method: "externally_identified_series",
          causalReadiness: "externally_identified",
          pointInTimeSafe: external.definition.pointInTimeSafe,
          note: `${external.definition.provider} · ${external.definition.method} · ${external.definition.sourceUrl}`
        },
        maxHorizonMonths: 12,
        minimumSamples: 36,
        note: "외부 shock strict gate를 통과한 식별 series입니다. 그러나 outcome/control의 PIT release-vintage 가용성은 별도 gate입니다."
      }));
    }
  }
  return { policyEventProxy, results };
}

function buildDefaultHypotheses(args: {
  officialHousingPrices?: IndicatorSeries[];
}, evidences: DirectionalTransmissionEvidence[], localProjections: LocalProjectionResult[]) {
  const ids = new Set(evidences.map((evidence) => evidence.id));
  const definitions: HypothesisDefinition[] = [];
  if (ids.has("edge-market5y-to-funding") && ids.has("edge-funding-to-mortgage")) {
    definitions.push({
      id: "market-to-mortgage-transmission",
      title: "시장금리가 은행을 거쳐 주담대로 전달되는가",
      claim: "시장금리 변화가 은행 신규조달비용을 거쳐 신규 주담대금리로 같은 방향 전달된다.",
      paths: [{ id: "bank-pricing", label: "시장금리 → 조달 → 주담대", description: "금융가격 전이의 기본 사슬", edges: [
        { edgeId: "edge-market5y-to-funding", expectedDirection: "positive" },
        { edgeId: "edge-funding-to-mortgage", expectedDirection: "positive" }
      ] }]
    });
  }
  if (ids.has("edge-market5y-to-policy")) {
    definitions.push({
      id: "market-rate-leads-policy",
      title: "시장금리가 기준금리를 선행하는가",
      claim: "국고채 5년 변화가 기준금리 변화보다 시간상 먼저 나타나는 경향이 우세하다.",
      paths: [{ id: "market-lead", label: "시장금리 → 정책금리", description: "반대방향 scan을 동시에 비교하는 선행성 가설", edges: [{ edgeId: "edge-market5y-to-policy", expectedDirection: "positive" }] }],
      note: "정책금리는 경기·물가에 내생적으로 반응하므로 선행성이 확인돼도 시장금리가 정책결정을 인과적으로 만든다는 뜻은 아닙니다."
    });
  }
  const capitalOfficial = evidences.find((evidence) => evidence.id.startsWith("mortgage-to-official-price-") && evidence.label.includes("수도권"));
  if (capitalOfficial) {
    definitions.push({
      id: "mortgage-direct-capital-price",
      title: "주담대금리의 수도권 주택가격 직접경로",
      claim: "주담대금리 상승 뒤 수도권 공식 아파트가격 변화가 반대 방향으로 나타나는 직접 금융경로가 존재한다.",
      paths: [{ id: "direct-price", label: "주담대금리 → 공식가격", description: "거래량·경기 중간경로를 생략한 직접 관계", edges: [{ edgeId: capitalOfficial.id, expectedDirection: "negative" }] }],
      note: "직접 edge가 약해졌다고 곧바로 경기 간접경로가 우세해졌다고 판단하지 않습니다. 별도 경기경로와 함께 비교해야 합니다."
    });
  }
  if (["edge-market5y-to-credit-spread", "edge-credit-spread-to-sentiment", "edge-sentiment-to-production", "edge-production-to-capital-price"].every((id) => ids.has(id))) {
    definitions.push({
      id: "macro-indirect-housing-path",
      title: "금리의 경기경로가 주택까지 이어지는가",
      claim: "금리 상승이 신용여건·심리·생산을 거쳐 수도권 주택가격에 간접 전달되는 경로가 관측된다.",
      paths: [{ id: "macro-path", label: "금리 → 신용 → 심리 → 생산 → 주택", description: "직접 주담대 경로와 구분하는 간접 경기경로", edges: [
        { edgeId: "edge-market5y-to-credit-spread", expectedDirection: "positive" },
        { edgeId: "edge-credit-spread-to-sentiment", expectedDirection: "negative" },
        { edgeId: "edge-sentiment-to-production", expectedDirection: "positive" },
        { edgeId: "edge-production-to-capital-price", expectedDirection: "positive" }
      ] }],
      note: "각 edge가 이어져도 공통 경기요인이나 주택→경기 역방향이 남을 수 있습니다. 경로 증거지도이지 구조모형의 mediation estimate가 아닙니다."
    });
  }
  for (const bandId of ["m3_30_50", "m4_ge_50"]) {
    const directId = `mortgage-to-prime4-volume-${bandId}`;
    const priceId = `prime4-volume-to-price-${bandId}`;
    if (!ids.has(directId) || !ids.has(priceId)) continue;
    const label = bandId === "m4_ge_50" ? "50억원 이상" : "30~50억원";
    definitions.push({
      id: `prime-direct-finance-${bandId}`,
      title: `고가주택(${label}) 직접 금융경로`,
      claim: `강남3구+용산 ${label} 시장에서 주담대금리 → 거래량 → 가격 proxy의 직접 금융경로가 유지되는가.`,
      paths: [{ id: "direct-high-end", label: "주담대 → 고가 거래 → 가격", description: "대출규제 이후 직접 금리노출 약화 여부를 보는 하나의 범용 경로 예시", edges: [
        { edgeId: directId, expectedDirection: "negative" },
        { edgeId: priceId, expectedDirection: "positive" }
      ] }],
      note: "특정 주장을 하드코딩한 결론이 아니라 가격대·지역·경로를 바꿔 재사용할 수 있는 hypothesis template입니다. RTMS 가격 proxy의 composition bias를 별도 경고합니다."
    });
  }
  return definitions.map((definition) => evaluateHypothesis(definition, evidences, localProjections));
}

export function buildHousingRelationshipAnalysis(args: {
  housing: HousingPolicyBundle;
  mortgageRate: IndicatorSeries;
  benchmarkRate?: IndicatorSeries | null;
  policyRate?: IndicatorSeries | null;
  policyRateDaily?: IndicatorSeries | null;
  eventMarketRateDaily?: IndicatorSeries | null;
  fundingRate?: IndicatorSeries | null;
  creditSpread?: IndicatorSeries | null;
  economicSentiment?: IndicatorSeries | null;
  industrialProduction?: IndicatorSeries | null;
  officialHousingPrices?: IndicatorSeries[];
  policyWatch?: PolicyWatchBundle | null;
  externalShockRegistry: ExternalShockRegistry;
}): HousingRelationshipAnalysis {
  const pairs = buildHousingLeadLagPairs({
    housing: args.housing,
    mortgageRate: args.mortgageRate,
    benchmarkRate: args.benchmarkRate,
    policyRate: args.policyRate,
    officialHousingPrices: args.officialHousingPrices,
    scope: "capital"
  });
  const safety = buildSafety(args.housing, args.mortgageRate, pairs);
  const relationships = safety.status === "blocked" ? [] : pairs.map((pair) => analyzeHousingRelationshipPair({ pair, housing: args.housing, policyWatch: args.policyWatch ?? null }));
  const directionalEvidence = relationships.map((relationship) => relationship.directionality);
  if (safety.status !== "blocked") directionalEvidence.push(...buildContextDirectionalEvidence(args));
  const localProjections = safety.status === "blocked" ? [] : buildLocalProjectionResults(args, pairs);
  const identified = buildIdentifiedShockLocalProjections({
    mortgageRate: args.mortgageRate,
    policyRateDaily: args.policyRateDaily,
    eventMarketRateDaily: args.eventMarketRateDaily,
    fundingRate: args.fundingRate,
    creditSpread: args.creditSpread,
    economicSentiment: args.economicSentiment,
    industrialProduction: args.industrialProduction,
    officialHousingPrices: args.officialHousingPrices,
    externalShockRegistry: args.externalShockRegistry
  });
  const pitEdgeReplays = args.policyRateDaily && args.eventMarketRateDaily ? [buildPitEdgeReplay({
    id: "pit-ktb1-to-policy",
    label: "국고채1Y → 한국은행 기준금리",
    leader: args.eventMarketRateDaily,
    follower: args.policyRateDaily
  })] : [];
  const pitReplayAudit = buildPitReplayAudit({
    series: [
      { series: args.eventMarketRateDaily },
      { series: args.policyRateDaily },
      { series: args.mortgageRate },
      { series: args.fundingRate },
      { series: args.creditSpread },
      { series: args.economicSentiment },
      { series: args.industrialProduction },
      ...args.externalShockRegistry.eligible.map((item) => ({ series: item.series, exactReleaseVintage: true }))
    ],
    housingSeries: args.officialHousingPrices,
    edgeReplays: pitEdgeReplays,
    externalRegistry: args.externalShockRegistry
  });
  const marketStates = buildMarketStateBundle({ housing: args.housing, officialHousingPrices: args.officialHousingPrices ?? [] });
  const nonlinearTransmission = buildNonlinearTransmissionBundle({ housing: args.housing, mortgageRate: args.mortgageRate, officialHousingPrices: args.officialHousingPrices ?? [] });
  const hysteresis = buildHysteresisBundle({ mortgageRate: args.mortgageRate, officialHousingPrices: args.officialHousingPrices ?? [] });
  const rateThresholdKink = buildRateThresholdKinkBundle({ mortgageRate: args.mortgageRate, officialHousingPrices: args.officialHousingPrices ?? [] });
  const spatialTransmission = buildSpatialTransmissionBundle({ housing: args.housing, mortgageRate: args.mortgageRate });
  const marketStateChangePoints = buildMarketStateChangePointBundle({ marketStates });
  const dataCapabilityMatrix = buildAnalysisDataCapabilityMatrix();
  const hypotheses = buildDefaultHypotheses(args, directionalEvidence, localProjections);
  const discoveries = discoverTransmissionCandidates(directionalEvidence, 12, localProjections, { marketStates, nonlinearTransmission, hysteresis, rateThresholdKink, spatialTransmission, marketStateChangePoints });
  return {
    generatedAt: new Date().toISOString(),
    safety,
    confirmedPolicies: confirmedHousingPolicyEvents(),
    watchedPolicyCandidates: args.policyWatch?.items ?? [],
    policyWatchStatus: args.policyWatch?.status ?? "not_collected",
    relationships,
    directionalEvidence,
    localProjections,
    identifiedShockLocalProjections: identified.results,
    shockIdentification: { policyEventProxy: identified.policyEventProxy, externalRegistry: args.externalShockRegistry },
    pitReplayAudit,
    marketStates,
    nonlinearTransmission,
    hysteresis,
    rateThresholdKink,
    spatialTransmission,
    marketStateChangePoints,
    dataCapabilityMatrix,
    hypotheses,
    discoveries,
    capabilities: {
      implemented: [
        "0M 동행 격리",
        "정방향/역방향 1~12M 동시 scan",
        "RTMS 거래량 YoY 계절성 방어",
        "3M moving-block permutation null envelope",
        "단일 best lag 대신 반응 집중구간",
        "범용 transmission graph / hypothesis evaluator / discovery layer",
        "관찰적 Local Projection 0~12M 누적반응",
        "lagged common-factor control + residual innovation proxy",
        "Newey-West(HAC) pointwise 95% interval + control-sensitivity gate",
        "정책금리 변경일 KTB1Y market-event proxy (event_proxy_only)",
        "검증 외부 shock strict registry gate",
        "PIT availability audit + daily proxy as-of replay",
        "Price × Volume market-state / divergence / thin-market confidence",
        "RTMS 법정동코드×법정동×단지명×전용면적 5㎡ fixed-cell composition defense",
        "금리 인상·인하 비대칭 방향성 scan",
        "인상/인하 × as-of trailing-relative 고금리/저금리 interaction LP",
        "확정 절대 주담대 한도 × 금리 interaction fail-closed gate",
        "동일 금리수준 인상기/인하기 hysteresis max-statistic permutation",
        "절대 금리 threshold/kink 전 cutoff block-permutation max-statistic",
        "서울↔경기 RTMS 양방향 spatial lead-lag + 공통 주담대금리 민감도 조정",
        "market-state retrospective change-point + 전 후보월 block-permutation max-statistic"
      ],
      pending: [
        "검증된 금통위 고빈도 surprise series의 기본 연결",
        "월별 거시/주택/RTMS release-vintage ledger",
        "simultaneous confidence band / block-bootstrap",
        "repeat-sales / 단지·면적·층·연식 fixed-cell composition 보정 고도화",
        "Price × Volume × Inventory (매물량 신규소스 필요)",
        "현금매수/실제 LTV·DSR buyer-financing channel (신규소스 필요)",
        "연체·경매 forced-sale channel (신규소스 필요)"
      ]
    },
    note: "v17.9는 v17.8의 spatial lead-lag를 유지하면서 market-state retrospective change-point를 추가합니다. 연속구간의 가능한 모든 break month에서 전·후 가격·거래 평균과 상태구성 차이를 탐색하고 3개월 block-permutation 최대통계량 95% 경계를 넘을 때만 구조변화 후보로 표시합니다. 양쪽 최소 12개월과 전체 연속구간 최소 36개월을 요구하며 full post-break sample을 사용하므로 실시간 trigger나 causal break date가 아닙니다."
  };
}
